import { useTheme, Theme } from '@/theme';
import { cn } from '@/lib/utils';

interface ThemeOptionProps {
  theme: Theme;
  currentTheme: Theme;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}

function ThemeOption({
  theme,
  currentTheme,
  onClick,
  icon,
  label,
}: ThemeOptionProps) {
  const isActive = currentTheme === theme;

  return (
    <button
      role="radio"
      aria-checked={isActive}
      onClick={onClick}
      className={cn(
        'p-2 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isActive
          ? 'bg-background shadow-sm text-foreground'
          : 'text-muted-foreground hover:bg-background/50 hover:text-foreground'
      )}
      aria-label={label}
    >
      {icon}
    </button>
  );
}

// Sun icon
function SunIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('w-4 h-4', className)}
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="m4.93 4.93 1.41 1.41" />
      <path d="m17.66 17.66 1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="m6.34 17.66-1.41 1.41" />
      <path d="m19.07 4.93-1.41 1.41" />
    </svg>
  );
}

// Moon icon
function MoonIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('w-4 h-4', className)}
      aria-hidden="true"
    >
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
    </svg>
  );
}

// Monitor/System icon
function MonitorIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('w-4 h-4', className)}
      aria-hidden="true"
    >
      <rect width="20" height="14" x="2" y="3" rx="2" />
      <line x1="8" x2="16" y1="21" y2="21" />
      <line x1="12" x2="12" y1="17" y2="21" />
    </svg>
  );
}

export function ThemeToggle() {
  const { theme, setTheme } = useTheme();

  return (
    <div
      role="radiogroup"
      aria-label="Theme selection"
      className="flex items-center gap-1 p-1 rounded-lg bg-muted"
    >
      <ThemeOption
        theme="light"
        currentTheme={theme}
        onClick={() => setTheme('light')}
        icon={<SunIcon />}
        label="Light theme"
      />
      <ThemeOption
        theme="dark"
        currentTheme={theme}
        onClick={() => setTheme('dark')}
        icon={<MoonIcon />}
        label="Dark theme"
      />
      <ThemeOption
        theme="system"
        currentTheme={theme}
        onClick={() => setTheme('system')}
        icon={<MonitorIcon />}
        label="System theme"
      />
    </div>
  );
}

// Simple toggle button for compact spaces
export function ThemeToggleButton() {
  const { resolvedTheme, toggleTheme } = useTheme();

  return (
    <button
      onClick={toggleTheme}
      className={cn(
        'p-2 rounded-md transition-colors',
        'hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      )}
      aria-label={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} theme`}
    >
      {resolvedTheme === 'dark' ? <SunIcon /> : <MoonIcon />}
    </button>
  );
}
